import { useCallback } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import {
  ConfigurationChangelogEntry,
  ConfigurationChangelogVersionEvent,
} from '@rossum/api-client/configurationChangelog';
import { Filters } from '@rossum/components/filtering';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { Tile, TileSkeleton } from '@rossum/components/tile';
import { TileChip, TileIconProps } from '@rossum/components/tile/types';
import { FeatureIconProps } from '@rossum/ui';
import {
  IconBolt,
  IconBuilding,
  IconCustomAurora,
  IconFileCode,
  IconFileUnknown,
  IconFolder,
  IconGavel,
  IconMail,
  IconWebhook,
  TablerIcon,
} from '@rossum/ui/icons/tabler';
import { Stack, Typography } from '@rossum/ui/material';
import { CONFIGURATION_CHANGELOG_QUERY } from '../../redux/modules/localStorage/actions';
import { HelmetComponent } from '../../routes/HelmetComponent';
import useDebounce from '../../utils/hooks/useDebounce';
import { useStoredQuery } from '../../utils/hooks/useStoredQuery';
import { useUnpaginatedHooks } from '../extensions/hooks/useUnpaginatedHooks';
import { useWorkspacesWithQueues } from '../queues/hooks/useWorkspacesWithQueues';
import { notify } from '../toaster';
import { ChangeSummary } from './ChangeSummary';
import { ConfigurationChangelogHeader } from './ConfigurationChangelogHeader';
import { FullTimestampTooltip } from './FullTimestampTooltip';
import { toConfigurationChangelogListQuery } from './hooks/toConfigurationChangelogListQuery';
import { useConfigurationChangelog } from './hooks/useConfigurationChangelog';
import {
  configurationChangelogQueryParser,
  useConfigurationChangelogFilterSettings,
} from './hooks/useConfigurationChangelogFilterSettings';
import {
  getUserName,
  useConfigurationChangelogUsers,
} from './hooks/useConfigurationChangelogUsers';
import { useInViewCallback } from './hooks/useInViewCallback';
import { configurationChangelogDetailPath } from './routes';

const DEFAULT_PAGE_SIZE = 20;

const objectTypeIconMap = {
  hook: IconWebhook,
  schema: IconFileCode,
  queue: IconFolder,
  rule: IconGavel,
  email_template: IconMail,
  trigger: IconBolt,
  workspace: IconBuilding,
  engine: IconCustomAurora,
} satisfies Record<string, TablerIcon>;

const isKnownObjectType = (
  value: string
): value is keyof typeof objectTypeIconMap =>
  Object.hasOwn(objectTypeIconMap, value);

const getObjectTypeLabel = (objectType: string, intl: IntlShape): string =>
  isKnownObjectType(objectType)
    ? intl.formatMessage({
        id: `features.configurationChangelog.objectType.${objectType}`,
      })
    : objectType;

const eventColorMap: Record<
  ConfigurationChangelogVersionEvent,
  FeatureIconProps['color']
> = {
  create: 'success',
  update: undefined,
  delete: 'error',
};

const getIconProps = (
  entry: ConfigurationChangelogEntry,
  tooltip: string
): TileIconProps => ({
  content: isKnownObjectType(entry.objectType)
    ? objectTypeIconMap[entry.objectType]
    : IconFileUnknown,
  color: eventColorMap[entry.versionEvent],
  size: 'small',
  tooltip,
});

export const ConfigurationChangelogPage = () => {
  const intl = useIntl();
  const [parsedParams, updateParsedParams] = useStoredQuery({
    parser: configurationChangelogQueryParser,
    localStorageKey: CONFIGURATION_CHANGELOG_QUERY,
  });

  const { usersById, isLoading: areUsersLoading } =
    useConfigurationChangelogUsers();

  const { data: hooks, isLoading: areHooksLoading } = useUnpaginatedHooks({
    pageSize: 100,
  });

  const { workspacesWithQueues, isLoading: areQueuesLoading } =
    useWorkspacesWithQueues({ enableQueries: true });

  const filterSettings = useConfigurationChangelogFilterSettings({
    setFilters: updateParsedParams,
    usersById,
    areUsersLoading,
    hooks,
    areHooksLoading,
    workspaces: workspacesWithQueues,
    areQueuesLoading,
  });

  const getModifierChips = useCallback(
    (modifierId: number | null): TileChip[] | undefined => {
      if (modifierId === null) return undefined;
      if (areUsersLoading) {
        return [{ key: 'modifier', label: '', isLoading: true }];
      }
      const user = usersById.get(modifierId);
      if (!user) {
        return [
          {
            key: 'modifier',
            label: intl.formatMessage({
              id: 'features.configurationChangelog.rows.modifier',
            }),
            tooltip: intl.formatMessage(
              { id: 'features.configurationChangelog.rows.modifierTooltip' },
              { userId: modifierId }
            ),
          },
        ];
      }
      const name = getUserName(user);
      const candidateTooltip = user.email ?? user.username;
      return [
        {
          key: 'modifier',
          label: user.deleted
            ? intl.formatMessage(
                { id: 'features.configurationChangelog.rows.modifierDeleted' },
                { name }
              )
            : name,
          tooltip: candidateTooltip !== name ? candidateTooltip : undefined,
        },
      ];
    },
    [areUsersLoading, usersById, intl]
  );

  const { search } = parsedParams ?? {};

  const debouncedSearch = useDebounce(search, 250);

  const clearFiltersKeepingSearch = useCallback(() => {
    updateParsedParams({
      hook: undefined,
      queue: undefined,
      versionCreatedAtAfter: undefined,
      versionCreatedAtBefore: undefined,
      objectType: undefined,
      versionEvent: undefined,
      modifierId: undefined,
      objectId: undefined,
    });
  }, [updateParsedParams]);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConfigurationChangelog({
    ...toConfigurationChangelogListQuery(parsedParams ?? {}),
    q: debouncedSearch || undefined,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const entries = data?.pages.flatMap(page => page.results) ?? [];

  const { ref: loadMoreRef } = useInViewCallback<HTMLDivElement>({
    onChange: inView => {
      if (inView && hasNextPage) {
        fetchNextPage().then(result => {
          if (result.isError) {
            notify.error({
              title: intl.formatMessage({
                id: 'features.configurationChangelog.rows.loadMoreError',
              }),
            });
          }
        });
      }
    },
  });

  return (
    <>
      <HelmetComponent translationKey="features.configurationChangelog.header.title" />
      <PageLayout
        renderHeader={params => (
          <ConfigurationChangelogHeader
            {...params}
            title={intl.formatMessage({
              id: 'features.configurationChangelog.header.title',
            })}
          />
        )}
      >
        <Stack spacing={1} p={4}>
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-start"
            >
              <Filters
                dataCy="configuration-changelog-filters"
                filters={parsedParams}
                setFilters={clearFiltersKeepingSearch}
                filterSettings={filterSettings}
              />
            </Stack>
            <SearchInput
              value={search}
              onChange={value =>
                updateParsedParams({ search: value || undefined })
              }
              placeholder={intl.formatMessage({
                id: 'features.configurationChangelog.search.placeholder',
              })}
            />
          </Stack>
          {isLoading ? (
            Array.from({ length: 8 }, (_, i) => (
              <TileSkeleton
                key={i}
                skeletons={['icon', 'title', 'description', 'endChips']}
              />
            ))
          ) : isError && entries.length === 0 ? (
            <Stack height="50vh" justifyContent="center" alignItems="center">
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({
                  id: 'features.configurationChangelog.rows.error',
                })}
              </Typography>
            </Stack>
          ) : entries.length === 0 ? (
            <Stack height="50vh" justifyContent="center" alignItems="center">
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({
                  id: 'features.configurationChangelog.rows.empty',
                })}
              </Typography>
            </Stack>
          ) : (
            <>
              {entries.map(entry => {
                const objectTypeLabel = getObjectTypeLabel(
                  entry.objectType,
                  intl
                );

                return (
                  <Tile
                    key={entry.versionId}
                    data-cy="configuration-changelog-row"
                    to={{
                      pathname: configurationChangelogDetailPath(
                        entry.versionId
                      ),
                      search: new URLSearchParams({
                        objectType: entry.objectType,
                        objectId: String(entry.objectId),
                      }).toString(),
                    }}
                    iconProps={getIconProps(entry, objectTypeLabel)}
                    title={
                      <Stack
                        direction="row"
                        alignItems="baseline"
                        gap={1}
                        sx={{ minWidth: 0, width: '100%' }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {entry.name ?? entry.objectId}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          flexShrink={0}
                        >
                          <FullTimestampTooltip
                            createdAt={entry.versionCreatedAt}
                          >
                            {intl.formatDate(new Date(entry.versionCreatedAt), {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })}
                          </FullTimestampTooltip>
                        </Typography>
                      </Stack>
                    }
                    description={
                      <ChangeSummary
                        versionEvent={entry.versionEvent}
                        objectTypeLabel={objectTypeLabel}
                        fields={entry.changedFields}
                      />
                    }
                    endChips={getModifierChips(entry.modifierId)}
                  />
                );
              })}
              <div ref={loadMoreRef} />
              {isFetchingNextPage &&
                Array.from({ length: 3 }, (_, i) => (
                  <TileSkeleton
                    key={i}
                    skeletons={['icon', 'title', 'description', 'endChips']}
                  />
                ))}
            </>
          )}
        </Stack>
      </PageLayout>
    </>
  );
};
